/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React, { useState, useMemo, useEffect } from 'react'
import { Alert } from 'antd'
import Text from '../../Text'
import Button from '../../Button'
import ProgressHeader from '../components/ProgressHeader'
import { useOnboardingWizard } from '../context/OnboardingWizardContext'
import colors from '@sportninja/common/constants/appColors'
import { TextInput } from '../../TextInput'
import { SelectInput } from '../../SelectInput'
import RangePicker from '../../RangePicker'
import DatePicker from '../../DatePicker'
import dayjs from 'dayjs'
import { SportEntry } from '../context/OnboardingWizardContext'
import req from '@sportninja/common/api/request'
import useWindowSize from 'src/hooks/useWindowSize'
import { trackOnboardingFirstCompetition } from 'src/utils/analytics'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY_300};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

const DatePickersRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

/**
 * First Competition step component - Step 3 of the onboarding wizard
 * Allows users to create their first competition.
 */
const FirstCompetition: React.FC = () => {
  console.log('🔄 FirstCompetition: Rendering step component')

  const {
    previousStep,
    completeWizard,
    skipWizard,
    sportsData,
    sports,
    competitionFormData,
    setCompetitionFormData,
  } = useOnboardingWizard()

  // Form State - initialize from saved data if available
  const [competitionName, setCompetitionName] = useState(
    competitionFormData?.name || ''
  )
  const [selectedLeagueEntry, setSelectedLeagueEntry] = useState<
    SportEntry | undefined
  >()
  const [selectedSportOverride, setSelectedSportOverride] = useState<
    string | null
  >(competitionFormData?.selectedSport || null)
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    competitionFormData?.startDate ? dayjs(competitionFormData.startDate) : null
  )
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(
    competitionFormData?.endDate ? dayjs(competitionFormData.endDate) : null
  )
  const [fieldErrors, setFieldErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Mobile detection
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 1024, [width])

  useEffect(() => {
    // Set initial league selection when data is available
    if (sportsData && sportsData.length > 0 && !selectedLeagueEntry) {
      // Try to restore from saved data first
      if (competitionFormData?.selectedLeague) {
        const savedLeague = sportsData.find(
          (league) => league.leagueName === competitionFormData.selectedLeague
        )
        if (savedLeague) {
          setSelectedLeagueEntry(savedLeague)
        } else {
          setSelectedLeagueEntry(sportsData[0])
        }
      } else {
        setSelectedLeagueEntry(sportsData[0])
      }
    }
  }, [sportsData, selectedLeagueEntry, competitionFormData])

  // Save form data to context whenever any field changes
  useEffect(() => {
    const formData = {
      name: competitionName,
      selectedLeague: selectedLeagueEntry?.leagueName || null,
      selectedSport: selectedSportOverride,
      startDate: startDate?.toISOString() || null,
      endDate: endDate?.toISOString() || null,
    }
    console.log('🔄 FirstCompetition: Saving form data', formData)
    setCompetitionFormData(formData)
  }, [
    competitionName,
    selectedLeagueEntry,
    selectedSportOverride,
    startDate,
    endDate,
    setCompetitionFormData,
  ])

  const leagueOptions = useMemo(() => {
    return (
      sportsData?.map((league) => ({
        label: league.leagueName,
        value: league.leagueName,
      })) || []
    )
  }, [sportsData])

  const sportOptions = useMemo(() => {
    return (
      sports?.map((sport) => ({
        label: sport.name_full,
        value: sport.name_full,
      })) || []
    )
  }, [sports])

  const selectedSportName = useMemo(() => {
    // If user has overridden the sport, use that
    if (selectedSportOverride) {
      return selectedSportOverride
    }

    // Otherwise, use the sport from the selected league
    if (!selectedLeagueEntry || !sports) {
      return ''
    }

    const league = selectedLeagueEntry
    const sport = sports.find(
      (s) =>
        (league.sport_id && s.sport_id === league.sport_id) ||
        (league.generic_sport_id &&
          s.generic_sport_id === league.generic_sport_id)
    )
    return sport ? sport.name_full : ''
  }, [selectedLeagueEntry, sports, selectedSportOverride])

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    console.log('🔄 FirstCompetition: Date range changed', dates)
    if (dates) {
      const [start, end] = dates
      setStartDate(start)
      setEndDate(end)
    } else {
      setStartDate(null)
      setEndDate(null)
    }
  }

  // Separate handlers for individual DatePickers
  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    console.log('🔄 FirstCompetition: Start date changed', date)
    setStartDate(date)
  }

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    console.log('🔄 FirstCompetition: End date changed', date)
    setEndDate(date)
  }

  const handleCreate = async () => {
    console.log('🔄 handleCreate: Starting competition creation')

    // Clear previous errors
    setError(null)
    // Basic validation
    const errors: any = {}
    if (!competitionName) {
      errors.competitionName = 'Competition name is required'
    }
    if (!selectedLeagueEntry) {
      errors.selectedLeague = 'Location is required'
    }
    if (!startDate || !endDate) {
      errors.dateRange = ['Date range is required']
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    // Find the sport data based on user selection (override or default)
    const selectedSport = sports?.find((s) => s.name_full === selectedSportName)

    const apiPayload = {
      name: competitionName,
      starts_at: startDate?.format('YYYY-MM-DD'),
      ends_at: endDate?.format('YYYY-MM-DD'),
      league_name: selectedLeagueEntry?.leagueName,
      // Include sport override data if user selected different sport
      sport_id: selectedSport?.sport_id || selectedLeagueEntry?.sport_id,
      generic_sport_id:
        selectedSport?.generic_sport_id ||
        selectedLeagueEntry?.generic_sport_id,
      leagues: sportsData
        ? sportsData.map((league) => ({
            name: league.leagueName,
            sport_id: selectedSportOverride
              ? selectedSport?.sport_id
              : league.sport_id,
            generic_sport_id: selectedSportOverride
              ? selectedSport?.generic_sport_id
              : league.generic_sport_id,
            is_generic: selectedSportOverride
              ? selectedSport?.is_generic
              : league.is_generic,
          }))
        : [],
    }
    console.log('🚀 Creating competition with data:', apiPayload)
    
    // Track the first competition creation
    trackOnboardingFirstCompetition({
      competition_name: competitionName,
      sport: selectedSportName,
      subleague: selectedLeagueEntry?.leagueName || '',
    })

    setLoading(true)
    try {
      const body = JSON.stringify(apiPayload)
      const response = await req('/wizard/league', { method: 'POST', body })
      console.log('✅ Competition created successfully', response)

      // Extract schedule ID from response
      const scheduleId = response?.data?.schedule?.id
      if (scheduleId) {
        console.log('🔄 Redirecting to schedule setup with ID:', scheduleId)
        completeWizard(scheduleId)
      } else {
        console.warn(
          '⚠️ No schedule ID found in response, completing without redirect'
        )
        completeWizard()
      }
    } catch (e: any) {
      console.error('🚨 handleCreate failed:', e)
      setError(e?.message || 'Failed to create competition. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const canProceed =
    competitionName && selectedLeagueEntry && startDate && endDate

  return (
    <StepContainer>
      <ProgressHeader currentStep={3} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Set Up Your First Competition!
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Fill in the details below to create your competition.
          </Text>
        </TitleSection>

        <FormContainer>
          <InputWrapper>
            <TextInput
              name='competitionName'
              value={competitionName}
              setValue={setCompetitionName}
              fieldErrors={fieldErrors}
              customError={{}}
              label='Competition Name *'
              placeholder="Summer Showdown '25"
              maxLength={100}
            />
          </InputWrapper>

          <InputRow>
            <InputWrapper>
              <SelectInput
                label='Where should this live?'
                name='selectedLeague'
                value={selectedLeagueEntry?.leagueName || ''}
                onChange={(leagueName) => {
                  const entry = sportsData?.find(
                    (l) => l.leagueName === leagueName
                  )
                  setSelectedLeagueEntry(entry)
                }}
                options={leagueOptions}
                placeholder='Select a location'
                fieldErrors={fieldErrors}
                customError={{}}
                disabled={!sportsData || sportsData.length === 0}
              />
            </InputWrapper>
            <InputWrapper>
              <SelectInput
                label='Sport'
                name='sport'
                value={selectedSportName}
                onChange={(sportName) => {
                  setSelectedSportOverride(sportName)
                }}
                options={sportOptions}
                placeholder='Select a sport'
                fieldErrors={fieldErrors}
                customError={{}}
                disabled={!sports || sports.length === 0}
              />
            </InputWrapper>
          </InputRow>

          <InputWrapper>
            {isMobile ? (
              <DatePickersRow>
                <DatePicker
                  label='Start Date *'
                  value={startDate}
                  onChange={handleStartDateChange}
                />
                <DatePicker
                  label='End Date *'
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate}
                />
              </DatePickersRow>
            ) : (
              <RangePicker
                label='Competition Dates *'
                name='dateRange'
                starts_at={startDate}
                ends_at={endDate}
                onChange={handleDateRangeChange}
                fieldErrors={fieldErrors.dateRange || []}
                customError={null}
              />
            )}
            {isMobile && fieldErrors.dateRange?.length > 0 && (
              <div
                css={css`
                  margin-top: 8px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.ERROR_300}
                >
                  {fieldErrors.dateRange[0]}
                </Text>
              </div>
            )}
          </InputWrapper>
        </FormContainer>

        {error && (
          <Alert
            message={error}
            type='error'
            showIcon
            style={{ marginTop: '8px' }}
          />
        )}

        <ActionsContainer>
          <NavigationButtons>
            <Button
              text='BACK'
              onClick={previousStep}
              variant='secondary'
              uppercase
              style={css`
                flex: 1;
              `}
            />
            <Button
              text={loading ? 'CREATING...' : 'CREATE'}
              onClick={handleCreate}
              variant='primary'
              uppercase
              disabled={!canProceed || loading}
              style={css`
                flex: 1;
              `}
            />
          </NavigationButtons>

          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default FirstCompetition
